import {
  doc,
  getDoc,
  getDocs,
  getDocsFromServer,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  where,
  type CollectionReference,
  type QueryConstraint,
  type Unsubscribe,
  type UpdateData,
  type WithFieldValue,
} from 'firebase/firestore'
import { getFirebaseApp } from '../config/firebase.config'
import { refs } from '../models/base/firestore-refs'
import {
  getOne,
  transactionSetWithBaseFields,
  transactionUpdateWithUpdatedAt,
} from './firestore-crud'
import {
  toPendingSchoolAccount,
  type PendingSchoolAccount,
  type PendingSchoolAccountDoc,
  type SchoolType,
} from '../models/account/school/pending-school-account.modl'
import {
  toPendingSchoolAccountHistoryEntity,
  type PendingSchoolAccountHistoryDoc,
  type PendingSchoolAccountHistoryEntity,
} from '../models/account/school/pending-school-account-history.modl'
import type { AdminAccount } from '../models/account/admin/admin-account.modl'
import { MailService } from './mail-service'
import { normalizePostalCodeForStorage } from '../utils/postal-code'

/** updatePendingSchoolAccountField で使う UI フィールド名 → Firestore フィールド名（単体テスト用に export） */
export const PENDING_SCHOOL_ACCOUNT_FIELD_MAP: Record<string, string> = {
  schoolType: 'school_type',
  schoolName: 'school_name',
  postalCode: 'postal_code',
  prefecture: 'prefecture',
  city: 'city',
  address: 'address',
  phone: 'phone',
  principal: 'principal',
  description: 'description',
  contactName: 'contact_name',
  contactEmail: 'contact_email',
}

/**
 * 仮登録申請のフィールド更新時の値を Firestore 用に正規化する（単体テスト用に export）
 * - schoolType: そのまま
 * - description: trim して空なら null
 * - その他: trim
 */
export function normalizePendingSchoolAccountFieldValue(field: string, value: string): string | null {
  if (field === 'schoolType') return value
  if (field === 'description') return value.trim() || null
  if (field === 'postalCode') return normalizePostalCodeForStorage(value)
  return value.trim()
}
/** 学校仮登録申請の作成用入力（school の仮登録フォームから） */
export interface CreatePendingSchoolAccountData {
  schoolType: SchoolType
  schoolName: string
  postalCode: string
  prefecture: string
  city: string
  address: string
  phone: string
  principal: string
  description?: string
  contactName: string
  contactEmail: string
}
/** 同一メールアドレスで既に学校アカウントが存在する場合に承認で投げるエラーメッセージ */
export const PENDING_APPROVE_DUPLICATE_EMAIL_MESSAGE =
  'このメールアドレスは既に学校アカウントで使用されています。'

/**
 * Firestore は undefined を許容しないため、履歴用に Doc の undefined を空文字・null に変換する
 */
function sanitizePendingSchoolAccountDocForWrite(
  doc: PendingSchoolAccountDoc | (PendingSchoolAccountDoc & { document_id?: string }),
): PendingSchoolAccountDoc & { document_id?: string } {
  return {
    ...doc,
    school_name: doc.school_name ?? '',
    postal_code: doc.postal_code ?? '',
    prefecture: doc.prefecture ?? '',
    city: doc.city ?? '',
    address: doc.address ?? '',
    phone: doc.phone ?? '',
    principal: doc.principal ?? '',
    description: doc.description ?? null,
    contact_name: doc.contact_name ?? '',
    contact_email: doc.contact_email ?? '',
  }
}

/**
 * PendingSchoolAccount（pending_school_accounts）用サービス
 */
export class PendingSchoolAccountService {
  /**
   * 仮登録申請を1件作成する（school の仮登録フォームから利用）
   * @returns 作成したドキュメントのID
   */
  async createPendingSchoolAccount(data: CreatePendingSchoolAccountData): Promise<string> {
    const db = getFirestore(getFirebaseApp())
    const now = Timestamp.now()
    const mailPayload = MailService.pendingSchoolRegistrationReceived({
      to: data.contactEmail,
      contactName: data.contactName,
      schoolName: data.schoolName,
      appliedAtText: now.toDate().toLocaleString('ja-JP'),
    })

    const id = await runTransaction(db, async (transaction) => {
      const pendingRef = transactionSetWithBaseFields(
        transaction,
        refs.pendingSchoolAccounts() as CollectionReference<PendingSchoolAccountDoc, PendingSchoolAccountDoc>,
        {
          school_type: data.schoolType,
          school_name: data.schoolName.trim(),
          postal_code: normalizePostalCodeForStorage(data.postalCode),
          prefecture: data.prefecture.trim(),
          city: data.city.trim(),
          address: data.address.trim(),
          phone: data.phone.trim(),
          principal: data.principal.trim(),
          description: data.description?.trim() || null,
          contact_name: data.contactName.trim(),
          contact_email: data.contactEmail.trim(),
          status: 'pending',
        } as WithFieldValue<PendingSchoolAccountDoc>,
        { isDeleted: false },
      )
      const mailRef = doc(refs.mail())
      transaction.set(mailRef, {
        ...mailPayload,
        memo: `pending_school_account: ${pendingRef.id}`,
      })
      return pendingRef.id
    })
    return id
  }

  /**
   * 仮登録申請をIDから1件取得する
   * @param id - 仮登録申請ドキュメントID
   * @param includeDeleted - 論理削除済みを含めるか（デフォルト: false）
   * @returns 取得した PendingSchoolAccount、存在しない場合は null
   */
  async getPendingSchoolAccount(
    id: string,
    includeDeleted: boolean = false,
  ): Promise<PendingSchoolAccount | null> {
    return getOne(
      refs.pendingSchoolAccount(id),
      (docId, data, path) => toPendingSchoolAccount(docId, data as PendingSchoolAccountDoc, path),
      { includeDeleted },
    )
  }

  /** 承認完了のポーリング間隔・タイムアウト（approvePendingSchoolAccount 用。エミュレータのトリガー待ちで長め） */
  private static readonly POLL_INTERVAL_MS = 500
  private static readonly POLL_TIMEOUT_MS = 90_000

  /**
   * 仮登録申請を「承認」して、学校アカウント作成リクエストを 1 件作成する（admin 編集）
   * トランザクションでリクエスト作成・pending 承認・履歴を確定し、トリガーが Auth 作成・school_accounts 作成・メール送信を行う。
   * 戻り値の uid / password はトリガー完了までポーリングして取得する（テスト用に testPassword を渡すと仮パスワードで作成される）。
   */
  async approvePendingSchoolAccount(params: {
    pendingSchoolAccount: PendingSchoolAccount
    adminAccount: AdminAccount
    /** テスト用。指定時はこのパスワードで Auth 作成し、トリガーが request に temp_password を書き戻す */
    testPassword?: string
  }): Promise<{ uid: string; updatedPendingSchoolAccount: PendingSchoolAccount | null; password: string }> {
    const { pendingSchoolAccount, adminAccount, testPassword } = params
    const normalizedEmail = pendingSchoolAccount.contactEmail.trim()

    const requestPayload: WithFieldValue<Record<string, unknown>> = {
      contact_email: normalizedEmail,
      school_type: pendingSchoolAccount.schoolType,
      school_name: pendingSchoolAccount.schoolName,
      postal_code: pendingSchoolAccount.postalCode,
      prefecture: pendingSchoolAccount.prefecture,
      city: pendingSchoolAccount.city,
      address: pendingSchoolAccount.address,
      phone: pendingSchoolAccount.phone,
      principal: pendingSchoolAccount.principal,
      description: pendingSchoolAccount.description ?? null,
      contact_name: pendingSchoolAccount.contactName,
      approved_by_admin_path: adminAccount.path ?? '',
      approved_by_admin_id: adminAccount.id,
      approved_by_admin_name: adminAccount.nameKanji ?? '',
      approved_by_admin_email: adminAccount.email ?? '',
      created_at: serverTimestamp(),
    }
    if (testPassword != null) {
      requestPayload.test_password = testPassword
    }

    const requestRef = refs.schoolAccountCreationRequest(pendingSchoolAccount.id)
    const pendingAccountDocRef = refs.pendingSchoolAccount(pendingSchoolAccount.id)
    const historyCollectionRef = refs.pendingSchoolAccountHistories(pendingSchoolAccount.id)

    // 同一メールの「rejected 以外」の他申請がある場合は承認不可
    const existingPendingQ = query(
      refs.pendingSchoolAccounts(),
      where('contact_email', '==', normalizedEmail),
      where('is_deleted', '==', false),
    )
    const existingPendingSnap = await getDocsFromServer(existingPendingQ)
    const hasOtherActivePending = existingPendingSnap.docs.some((d) => {
      if (d.id === pendingSchoolAccount.id) return false
      const status = (d.data() as PendingSchoolAccountDoc).status
      return status !== 'rejected'
    })
    if (hasOtherActivePending) {
      throw new Error('このメールアドレスは既に仮登録申請が存在しています（未削除）')
    }

    // 同一メールの学校アカウントが既に存在する場合は承認不可
    const existingSchoolQ = query(refs.schoolAccounts(), where('contact_email', '==', normalizedEmail))
    const existingSchoolSnap = await getDocsFromServer(existingSchoolQ)
    if (!existingSchoolSnap.empty) {
      throw new Error(PENDING_APPROVE_DUPLICATE_EMAIL_MESSAGE)
    }

    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      const pendingSnap = await transaction.get(pendingAccountDocRef)
      const pendingStatus = pendingSnap.data()?.status
      if (pendingStatus !== 'pending') {
        throw new Error('この仮登録は既に承認または却下されています')
      }
      const pendingDoc = pendingSnap.data() as PendingSchoolAccountDoc

      transaction.set(requestRef, requestPayload)
      transactionUpdateWithUpdatedAt(
        transaction,
        pendingAccountDocRef,
        {
          status: 'approved',
          approved_at: serverTimestamp(),
          approved_by: refs.adminAccount(adminAccount.id),
        } as UpdateData<PendingSchoolAccountDoc>,
      )
      transactionSetWithBaseFields(
        transaction,
        historyCollectionRef as CollectionReference<PendingSchoolAccountHistoryDoc, PendingSchoolAccountHistoryDoc>,
        {
          pending_school_account: sanitizePendingSchoolAccountDocForWrite(pendingDoc),
          updated_by_user_type: 'admin',
          updated_by_user_id: adminAccount.id,
          updated_by_name: adminAccount.nameKanji ?? '',
          updated_by_user_document_path: adminAccount.path ?? '',
          update_info: '承認',
        } as WithFieldValue<PendingSchoolAccountHistoryDoc>,
        { isDeleted: false },
      )
    })

    const updatedSnap = await getDoc(pendingAccountDocRef)
    const updatedPendingSchoolAccount = updatedSnap.exists()
      ? toPendingSchoolAccount(updatedSnap.id, updatedSnap.data() as PendingSchoolAccountDoc, updatedSnap.ref.path)
      : null

    let uid = ''
    let password = ''
    const deadline = Date.now() + PendingSchoolAccountService.POLL_TIMEOUT_MS
    while (Date.now() < deadline) {
      const requestSnap = await getDoc(requestRef)
      const d = requestSnap.data() as { uid?: string; temp_password?: string } | undefined
      if (d?.uid) {
        uid = d.uid
        password = d.temp_password ?? ''
        break
      }
      await new Promise((r) => setTimeout(r, PendingSchoolAccountService.POLL_INTERVAL_MS))
    }

    if (!uid) {
      throw new Error('学校アカウントの作成がタイムアウトしました。しばらく経ってから一覧を確認してください。')
    }

    return { uid, updatedPendingSchoolAccount, password }
  }

  /**
   * 仮登録申請一覧を購読する（未削除のみ、登録日降順）
   * admin の学校仮登録一覧などで利用
   */
  subscribePendingSchoolAccounts(
    onNext: (accounts: PendingSchoolAccount[]) => void,
    onError?: (error: Error) => void,
  ): Unsubscribe {
    const q = query(
      refs.pendingSchoolAccounts(),
      where('is_deleted', '==', false),
      orderBy('created_at', 'desc'),
    )
    // キャッシュのみのスナップショットはスキップする（別ユーザーに切り替えた直後など、
    // 古いトークンで取った結果が一瞬返りセキュリティルールと食い違うのを防ぐ）
    return onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snapshot) => {
        if (snapshot.metadata.fromCache) {
          return
        }
        const accounts = snapshot.docs.map((d) =>
          toPendingSchoolAccount(d.id, d.data() as PendingSchoolAccountDoc, d.ref.path),
        )
        onNext(accounts)
      },
      (err) => {
        const e = err instanceof Error ? err : new Error('仮登録申請の取得に失敗しました')
        if (onError) onError(e)
      },
    )
  }

  /**
   * 仮登録申請の履歴一覧を取得する（created_at 降順）
   */
  async getPendingSchoolAccountHistories(
    pendingSchoolAccountId: string,
  ): Promise<PendingSchoolAccountHistoryEntity[]> {
    const historyRef = refs.pendingSchoolAccountHistories(pendingSchoolAccountId)
    const q = query(
      historyRef,
      where('is_deleted', '==', false),
      orderBy('created_at', 'desc'),
    )
    const snap = await getDocs(q)
    return snap.docs.map((d) =>
      toPendingSchoolAccountHistoryEntity(d.id, d.data() as PendingSchoolAccountHistoryDoc, d.ref.path),
    )
  }

  /**
   * 仮登録申請の単一フィールドを更新する（履歴作成＋更新＋再取得）
   * @param account - 更新対象の仮登録申請
   * @param field - フィールド名（schoolType, schoolName, postalCode, prefecture, city, address, phone, principal, description, contactName, contactEmail）
   * @param value - 更新する値
   * @param adminAccount - 実行する管理者
   * @param updateInfoLabel - 履歴に記録する更新内容ラベル（例: '学校名'）
   * @returns 更新後の PendingSchoolAccount、失敗時は null
   */
  async updatePendingSchoolAccountField(
    account: PendingSchoolAccount,
    field: string,
    value: string,
    adminAccount: AdminAccount,
    updateInfoLabel: string,
  ): Promise<PendingSchoolAccount | null> {
    const firestoreField = PENDING_SCHOOL_ACCOUNT_FIELD_MAP[field]
    if (!firestoreField) return null

    const accountRef = refs.pendingSchoolAccount(account.id)
    const historyRef = refs.pendingSchoolAccountHistories(account.id)

    // Firestore は undefined を許容しないため、古いデータで欠けている prefecture/city 等は空文字にフォールバック
    const pendingSnapshot: PendingSchoolAccountDoc & { document_id?: string } = {
      school_type: account.schoolType,
      school_name: account.schoolName ?? '',
      postal_code: account.postalCode ?? '',
      prefecture: account.prefecture ?? '',
      city: account.city ?? '',
      address: account.address ?? '',
      phone: account.phone ?? '',
      principal: account.principal ?? '',
      description: account.description ?? null,
      contact_name: account.contactName ?? '',
      contact_email: account.contactEmail ?? '',
      status: account.status,
      approved_at: account.approvedAt ? Timestamp.fromDate(account.approvedAt) : null,
      rejected_at: account.rejectedAt ? Timestamp.fromDate(account.rejectedAt) : null,
      approved_by: account.approvedBy ? refs.adminAccount(account.approvedBy) : null,
      rejected_by: account.rejectedBy ? refs.adminAccount(account.rejectedBy) : null,
      rejection_reason: account.rejectionReason ?? null,
      created_at: Timestamp.fromDate(account.created_at),
      updated_at: Timestamp.fromDate(account.updated_at),
      is_deleted: account.is_deleted,
      document_id: account.id,
    }

    const rawValue =
      field === 'schoolType'
        ? value
        : field === 'description'
          ? value.trim() || null
          : field === 'postalCode'
            ? normalizePostalCodeForStorage(value)
            : value.trim()
    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      transactionSetWithBaseFields(
        transaction,
        historyRef as CollectionReference<PendingSchoolAccountHistoryDoc, PendingSchoolAccountHistoryDoc>,
        {
          pending_school_account: pendingSnapshot,
          updated_by_user_type: 'admin',
          updated_by_user_id: adminAccount.id,
          updated_by_name: adminAccount.nameKanji ?? '',
          updated_by_user_document_path: adminAccount.path ?? '',
          update_info: updateInfoLabel,
        } as WithFieldValue<PendingSchoolAccountHistoryDoc>,
        { isDeleted: false },
      )
      transactionUpdateWithUpdatedAt(transaction, accountRef, {
        [firestoreField]: rawValue,
      } as UpdateData<PendingSchoolAccountDoc>)
    })

    return this.getPendingSchoolAccount(account.id)
  }

  /**
   * 仮登録申請を検索する（メール・電話・学校名のいずれかで完全一致）
   * @param searchText - 検索文字列（空の場合は空配列を返す）
   * @returns 一致した PendingSchoolAccount の配列（created_at 降順）
   */
  async searchPendingSchoolAccounts(searchText: string): Promise<PendingSchoolAccount[]> {
    const trimmed = searchText.trim()
    if (!trimmed) return []

    const baseConstraints: QueryConstraint[] = [where('is_deleted', '==', false)]
    const [emailSnap, phoneSnap, schoolNameSnap] = await Promise.all([
      getDocs(
        query(
          refs.pendingSchoolAccounts(),
          ...baseConstraints,
          where('contact_email', '==', trimmed),
          orderBy('created_at', 'desc'),
        ),
      ),
      getDocs(
        query(
          refs.pendingSchoolAccounts(),
          ...baseConstraints,
          where('phone', '==', trimmed),
          orderBy('created_at', 'desc'),
        ),
      ),
      getDocs(
        query(
          refs.pendingSchoolAccounts(),
          ...baseConstraints,
          where('school_name', '==', trimmed),
          orderBy('created_at', 'desc'),
        ),
      ),
    ])

    const merged = new Map<string, PendingSchoolAccount>()
    for (const snap of [emailSnap, phoneSnap, schoolNameSnap]) {
      for (const d of snap.docs) {
        if (!merged.has(d.id)) {
          merged.set(d.id, toPendingSchoolAccount(d.id, d.data() as PendingSchoolAccountDoc, d.ref.path))
        }
      }
    }

    return [...merged.values()].sort((a, b) => b.created_at.getTime() - a.created_at.getTime())
  }

  /**
   * 仮登録申請を検索する
   * - メール: 完全一致
   * - 電話番号/学校名: 前方一致
   * - 複数条件指定時は AND
   * @param params - 検索条件（いずれか1つ以上指定必須）
   * @returns 一致した PendingSchoolAccount の配列（created_at 降順）
   */
  async searchPendingSchoolAccountsByFields(params: {
    email?: string
    phone?: string
    schoolName?: string
  }): Promise<PendingSchoolAccount[]> {
    const email = params.email?.trim() ?? ''
    const phone = params.phone?.trim() ?? ''
    const schoolName = params.schoolName?.trim() ?? ''
    if (!email && !phone && !schoolName) return []

    const constraints: QueryConstraint[] = [where('is_deleted', '==', false)]
    if (email) constraints.push(where('contact_email', '==', email))

    // Firestore は異なる複数フィールドへの range 条件を1クエリで扱えないため、
    // 電話/学校名の前方一致はどちらか一方をクエリに使い、残りは取得後に絞り込む。
    const usePhonePrefix = !!phone && (!schoolName || phone.length >= schoolName.length)
    const useSchoolNamePrefix = !!schoolName && !usePhonePrefix

    if (usePhonePrefix) {
      constraints.push(where('phone', '>=', phone))
      constraints.push(where('phone', '<=', `${phone}\uf8ff`))
      constraints.push(orderBy('phone'))
    } else if (useSchoolNamePrefix) {
      constraints.push(where('school_name', '>=', schoolName))
      constraints.push(where('school_name', '<=', `${schoolName}\uf8ff`))
      constraints.push(orderBy('school_name'))
    } else {
      constraints.push(orderBy('created_at', 'desc'))
    }

    const q = query(refs.pendingSchoolAccounts(), ...constraints)
    const snapshot = await getDocs(q)
    const data = snapshot.docs.map((d) =>
      toPendingSchoolAccount(d.id, d.data() as PendingSchoolAccountDoc, d.ref.path),
    )

    return data
      .filter((a) => {
        if (phone && !a.phone.startsWith(phone)) return false
        if (schoolName && !a.schoolName.startsWith(schoolName)) return false
        return true
      })
      .sort((a, b) => b.created_at.getTime() - a.created_at.getTime())
  }

  /**
   * 仮登録申請を却下する（履歴作成・ステータス更新）
   * 却下時のチャット通知は Firebase の onUpdate トリガーで送信する。
   * @param pendingAccount - 却下対象の仮登録申請
   * @param adminAccount - 実行する管理者
   * @param rejectionReason - 却下理由
   * @returns 更新後の PendingSchoolAccount、失敗時は null
   */
  async rejectPendingSchoolAccount(
    pendingAccount: PendingSchoolAccount,
    adminAccount: AdminAccount,
    rejectionReason: string,
  ): Promise<PendingSchoolAccount | null> {
    if (pendingAccount.status === 'rejected') {
      return null
    }

    const nowTs = Timestamp.now()
    const accountId = pendingAccount.id

    // Firestore は undefined を許容しないため、古いデータで欠けている prefecture/city 等は空文字にフォールバック
    const pendingSnapshot: PendingSchoolAccountDoc & { document_id?: string } = {
      school_type: pendingAccount.schoolType,
      school_name: pendingAccount.schoolName ?? '',
      postal_code: pendingAccount.postalCode ?? '',
      prefecture: pendingAccount.prefecture ?? '',
      city: pendingAccount.city ?? '',
      address: pendingAccount.address ?? '',
      phone: pendingAccount.phone ?? '',
      principal: pendingAccount.principal ?? '',
      description: pendingAccount.description ?? null,
      contact_name: pendingAccount.contactName ?? '',
      contact_email: pendingAccount.contactEmail ?? '',
      status: 'rejected',
      approved_at: pendingAccount.approvedAt ? Timestamp.fromDate(pendingAccount.approvedAt) : null,
      rejected_at: nowTs,
      approved_by: pendingAccount.approvedBy ? refs.adminAccount(pendingAccount.approvedBy) : null,
      rejected_by: refs.adminAccount(adminAccount.id),
      rejection_reason: rejectionReason.trim(),
      created_at: Timestamp.fromDate(pendingAccount.created_at),
      updated_at: nowTs,
      is_deleted: pendingAccount.is_deleted,
      document_id: accountId,
    }

    const historyRef = refs.pendingSchoolAccountHistories(accountId)
    const pendingDocRef = refs.pendingSchoolAccount(accountId)
    const db = getFirestore(getFirebaseApp())
    await runTransaction(db, async (transaction) => {
      transactionSetWithBaseFields(
        transaction,
        historyRef as CollectionReference<PendingSchoolAccountHistoryDoc, PendingSchoolAccountHistoryDoc>,
        {
          pending_school_account: pendingSnapshot,
          updated_by_user_type: 'admin',
          updated_by_user_id: adminAccount.id,
          updated_by_name: adminAccount.nameKanji ?? '',
          updated_by_user_document_path: adminAccount.path ?? '',
          update_info: '本登録却下',
        } as WithFieldValue<PendingSchoolAccountHistoryDoc>,
        { isDeleted: false },
      )
      transactionUpdateWithUpdatedAt(transaction, pendingDocRef, {
        status: 'rejected',
        rejected_at: nowTs,
        rejected_by: refs.adminAccount(adminAccount.id),
        rejection_reason: rejectionReason.trim(),
      } as UpdateData<PendingSchoolAccountDoc>)
    })

    const snap = await getDoc(pendingDocRef)
    if (!snap.exists()) return null
    return toPendingSchoolAccount(snap.id, snap.data() as PendingSchoolAccountDoc, snap.ref.path)
  }

  /**
   * メールアドレスで is_deleted=false かつ rejected 以外の仮登録申請が存在するかチェック
   * @param email - チェックするメールアドレス
   * @param excludeId - チェックから除外するドキュメントID（現在編集中のものなど）
   * @returns 存在する場合 true、存在しない場合 false
   */
  async checkEmailExists(email: string, excludeId?: string): Promise<boolean> {
    const q = query(
      refs.pendingSchoolAccounts(),
      where('status', '!=', 'rejected'),
      where('contact_email', '==', email.trim()),
      where('is_deleted', '==', false),
    )

    // 承認/却下直後でもキャッシュではなく最新状態を参照する
    const snapshot = await getDocsFromServer(q)
    const docs = snapshot.docs.filter((d) => d.id !== excludeId)
    return docs.length > 0
  }
}
